/**
 * `useMarkResetOnNavigation` — implements Google-Docs-style "new typing starts
 * unformatted" behavior. Plate's default keeps the active inline marks alive
 * across Enter, arrow-key navigation, and bare clicks; this hook clears them
 * so the user doesn't keep typing in bold/italic/color after moving the caret.
 *
 * What it owns
 *   - `MARK_KEYS_TO_RESET`: the set of mark names cleared on navigation
 *     (bold/italic/underline/strikethrough/code/highlight + colors + fonts +
 *     kbd + sub/superscript). Inline structural marks like links are NOT in
 *     this list and survive navigation.
 *   - `markResetTimerRef`: a setTimeout handle for the 250 ms debounce on
 *     click resets — covered below.
 *
 * What it exposes
 *   - `clearMarksAtCaret()` — direct call to clear marks NOW. Used by:
 *      - the keyboard hook on Enter (via a microtask defer so the new line
 *        is created before marks are stripped),
 *      - the keyup handler here on arrow / Home / End / PageUp / PageDown,
 *      - the click handler here after the 250 ms debounce settles.
 *   - `cancelPendingMarkReset()` — cancels any scheduled clear-from-click.
 *     Used by:
 *      - the click handler itself (every new click resets the timer),
 *      - the editor's padding-area click handler (so clicking outside the
 *        content doesn't race with the timer),
 *      - the unmount cleanup.
 *   - `handleKeyUpReset` — wire to `<PlateContent onKeyUp>`. Schedules a
 *     clear on next RAF if the released key was a navigation key AND the
 *     selection is collapsed. RAF defer so the caret position has settled.
 *   - `handleClickReset` — wire to `<PlateContent onMouseUp>`. Ignores
 *     multi-clicks (`e.detail > 1` = double-click for word select), then
 *     schedules a clear 250 ms later. The delay lets a dblclick cancel
 *     before the reset races with the word-selection that dblclick
 *     creates — without it, you'd lose your formatting marks every time
 *     you double-clicked to select a word.
 *
 * The bit-twiddling inside `clearMarksAtCaret`
 *   Plate stores active marks at `editor.marks`. We can't use the public
 *   transform API (no `tf.clearMark` that clears multiple keys atomically),
 *   so we mutate the marks object directly and call `editor.onChange()` to
 *   notify Plate. Hot-path performance was the original reason — the API
 *   path costs ~4× more for what's a single object key delete. Comment
 *   flagged in the original code as risky on version upgrades; if a future
 *   Plate refactor changes how marks are stored, this is the first place
 *   to revisit.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

// 250 ms is the minimum spacing between separate clicks at default browser
// double-click thresholds (Chrome/Firefox/Safari all use 250-500 ms). Below
// that, dblclick fires after the second click's mouseup, so a single-click
// reset would race the dblclick word-selection.
const CLICK_RESET_DEBOUNCE_MS = 250;

// Navigation keys that should clear marks on release. Excludes editing keys
// (letters/digits/Enter/Backspace etc.) because those produce text that
// belongs in the active formatting.
const NAV_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']);

export interface UseMarkResetReturn {
    clearMarksAtCaret: () => void;
    cancelPendingMarkReset: () => void;
    handleKeyUpReset: (e: React.KeyboardEvent) => void;
    handleClickReset: (e: React.MouseEvent) => void;
}

export function useMarkResetOnNavigation(editor: any): UseMarkResetReturn {
    const markResetTimerRef = useRef<number | null>(null);

    const MARK_KEYS_TO_RESET = useMemo(
        () => [
            // Color/highlight marks — clear when the caret leaves a tinted
            // region so new text doesn't inherit the highlight the user
            // visually walked away from.
            'highlight',
            'color',
            'backgroundColor',
            // Code / kbd / sub/super — uncommon stylings users toggle on for
            // a short run and rarely want to carry across blocks.
            'code',
            'kbd',
            'subscript',
            'superscript',
            // bold / italic / underline / strikethrough / fontSize /
            // fontFamily intentionally NOT reset — those represent the
            // user's typing intent for the current run and should persist
            // across Enter / navigation / clicks (Word/Google Docs behavior).
        ],
        [],
    );

    const cancelPendingMarkReset = useCallback(() => {
        if (markResetTimerRef.current != null) {
            window.clearTimeout(markResetTimerRef.current);
            markResetTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => cancelPendingMarkReset(), [cancelPendingMarkReset]);

    const clearMarksAtCaret = useCallback(() => {
        const ed: any = editor;
        if (!ed) return;
        try {
            // Combine the pending `editor.marks` override AND the marks of the
            // leaf to the left of the caret. `ed.api.marks()` is slate's
            // canonical "what marks would the next typed character have?";
            // checking only `ed.marks` would miss the leaf-inherited case (caret
            // sitting at the trailing edge of a highlighted run with no
            // override set — the next character would still get the
            // highlight from the leaf, but `ed.marks` is null so the old code
            // bailed).
            const active: Record<string, any> | null = ed.api?.marks?.() ?? ed.marks ?? null;
            if (!active) return;

            const cleaned: Record<string, any> = {};
            let changed = false;
            for (const [k, v] of Object.entries(active)) {
                if (MARK_KEYS_TO_RESET.includes(k)) {
                    changed = true;
                } else {
                    cleaned[k] = v;
                }
            }
            if (!changed) return;

            // Set the override even if `cleaned` is empty (`{}`) — an empty
            // override is what tells slate "the next typed character has no
            // marks", overriding leaf inheritance. Setting `null` here would
            // let the leaf's marks come back.
            ed.marks = cleaned;
            // Force Plate to notify subscribers — toolbar active-states need
            // to flip off immediately, not on the next real edit.
            ed.onChange();
        } catch {
            /* some marks may not be registered */
        }
    }, [editor, MARK_KEYS_TO_RESET]);

    const isCollapsedSelection = useCallback(() => {
        const ed: any = editor;
        const sel = ed?.selection;
        if (!sel) return false;
        return JSON.stringify(sel.anchor?.path) === JSON.stringify(sel.focus?.path) && sel.anchor?.offset === sel.focus?.offset;
    }, [editor]);

    const handleClickReset = useCallback(
        (e: React.MouseEvent) => {
            // Multi-click (double / triple) creates a word/line selection.
            // Mark reset would clobber the selection's active formatting
            // before the user can do anything with it.
            if (e.detail > 1) return;
            // Every new click resets the timer — most recent click wins.
            cancelPendingMarkReset();
            markResetTimerRef.current = window.setTimeout(() => {
                markResetTimerRef.current = null;
                // Re-check at fire time — a dblclick may have happened in
                // the intervening 250 ms and we'd be wrong to clear marks.
                if (isCollapsedSelection()) clearMarksAtCaret();
            }, CLICK_RESET_DEBOUNCE_MS);
        },
        [clearMarksAtCaret, cancelPendingMarkReset, isCollapsedSelection],
    );

    const handleKeyUpReset = useCallback(
        (e: React.KeyboardEvent) => {
            if (!NAV_KEYS.has(e.key)) return;
            // RAF defer so editor.selection has settled to the new caret
            // position before we check isCollapsedSelection.
            requestAnimationFrame(() => {
                if (isCollapsedSelection()) clearMarksAtCaret();
            });
        },
        [clearMarksAtCaret, isCollapsedSelection],
    );

    return { clearMarksAtCaret, cancelPendingMarkReset, handleKeyUpReset, handleClickReset };
}
